import { serverFetch } from './serverFetch';

const BACKEND_URL = process.env.NEXT_PUBLIC_API_BACKEND || 'https://sca-backend.digitaldevworkspace.com';

export interface OrganizerEvent {
  id?: string;
  _id?: string;
  title?: string;
  name?: string;
  description?: string;
  summary?: string;
  startDate?: string;
  endDate?: string;
  start_time?: string;
  end_time?: string;
  startTime?: string;
  endTime?: string;
  dayOfWeek?: string;
  [key: string]: any;
}

export interface EventsByOrganizerResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: OrganizerEvent[];
  meta?: {
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    [key: string]: any;
  };
}

export async function getEventsByOrganizer(
  organizerId: string,
  token?: string,
  page?: number,
  limit?: number
): Promise<EventsByOrganizerResponse> {
  const params = new URLSearchParams();
  if (page) params.set('page', String(page));
  if (limit) params.set('limit', String(limit));
  // Always skip past events unless specified otherwise
  params.set('skipPastEvents', 'true');
  const qs = params.toString();
  const url = `${BACKEND_URL}/api/v1/events/organiser/${organizerId}${qs ? `?${qs}` : ''}`;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, { headers, method: 'GET' });
  if (!res.ok) {
    let errBody: any = {};
    try { errBody = await res.json(); } catch {}
    throw new Error(errBody?.message || `Failed to fetch events: ${res.status}`);
  }
  return res.json();
}

/**
 * Events API utilities
 */

import { BackendEventsResponse, EventsApiResponse } from '@/lib/mappers/eventsMapper';
import { fetchApi } from './client';

/**
 * Fetches events data from the real backend API
 */
export async function fetchEventsData(): Promise<BackendEventsResponse> {
  const backendUrl = process.env.NEXT_PUBLIC_API_BACKEND || 'https://sca-backend.digitaldevworkspace.com';
  const response = await fetch(`${backendUrl}/api/v1/events`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch events from backend: ${response.statusText}`);
  }
  
  return await response.json();
}

/**
 * Converts legacy JSON format to backend format
 */
function convertLegacyToBackendFormat(legacyData: any): BackendEventsResponse {
  return {
    success: true,
    statusCode: 200,
    message: "Events retrieved successfully",
    data: legacyData.events.map((event: any) => ({
      id: event.id.toString(),
      name: event.title,
      place: event.location,
      timing: {
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        timezone: 'UTC'
      },
      contactInformations: {
        email: 'contact@example.com',
        phone: '123-456-7890',
        website: '',
        address: {
          address: event.location,
          city: 'Unknown',
          state: 'Unknown',
          zip: '00000',
          country: 'Unknown',
          fullAddress: event.location
        },
        socialMedia: {
          facebook: '',
          twitter: ''
        }
      },
      description: event.description,
      mainImage: event.image.src,
      categories: [{
        _id: 'legacy-category',
        name: event.category,
        key: event.category.toLowerCase().replace(/\s+/g, '-'),
        isActive: true,
        isDeleted: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        showOnSite: true
      }],
      organiser: {
        _id: 'legacy-organizer',
        name: event.organization,
        approvalStatus: 'approved',
        isActive: true,
        isDeleted: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        showOnSite: true
      },
      rsvps: [],
      eventFees: 'free',
      eventRegions: 'local',
      approvalStatus: 'approved',
      isActive: true,
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    })),
    meta: {
      requestId: "fallback-request-id",
      timestamp: new Date().toISOString(),
      duration: 0,
      pagination: {
        page: "1",
        limit: legacyData.events.length.toString(),
        total: legacyData.events.length,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
      },
      path: "/api/v1/events",
      method: "GET",
      userAgent: "Fallback-Data",
      ip: "127.0.0.1"
    }
  };
}

/**
 * Server-side function to get events data from backend
 */
export async function getEventsData(): Promise<BackendEventsResponse> {
  try {
    // Always try to fetch from real backend first
    const backendUrl = process.env.NEXT_PUBLIC_API_BACKEND || 'https://sca-backend.digitaldevworkspace.com';
    
    const response = await serverFetch(`${backendUrl}/api/v1/events`, {
      cache: 'no-store', // Ensure fresh data
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch events from backend: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching events data from backend:', error);
    // Fallback to direct import if fetch fails
    try {
      const eventsData = await import('@/data/events.json');
      return convertLegacyToBackendFormat(eventsData.default);
    } catch (importError) {
      console.error('Error importing events data:', importError);
      throw error;
    }
  }
}
